import { ApiError } from '@berky/auth'
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Textarea,
  toast,
} from '@berky/ui'
import { useDeferredValue, useState } from 'react'
import { Link } from 'react-router'
import {
  useFeedback,
  useFeedbackDuplicates,
  useFeedbackDuplicateTargets,
  useFeedbackDuplicateChange,
  useFeedbackPublishProgress,
} from '../api'
import type { FeedbackEntry, FeedbackDuplicateItem, LeadingProgress } from '../types'
import { FeedbackStatusBadge } from './feedback-status'

export function FeedbackProgressSummary({
  entry,
}: {
  entry: { leadingProgress: LeadingProgress | null; publicProgressNote: string | null }
}) {
  return (
    <div className="space-y-2 text-sm">
      {entry.publicProgressNote && (
        <div>
          <p className="font-medium">Freigegebener Fortschritt</p>
          <p className="whitespace-pre-wrap break-words">{entry.publicProgressNote}</p>
        </div>
      )}
      {entry.leadingProgress && (
        <div className="space-y-2 rounded-md border bg-muted/30 p-3">
          <p className="font-medium">Fortschritt des führenden Feedbacks</p>
          <FeedbackStatusBadge status={entry.leadingProgress.status} />
          {entry.leadingProgress.note && (
            <p className="whitespace-pre-wrap break-words">
              {entry.leadingProgress.note}
            </p>
          )}
          <p className="text-xs text-muted-foreground">
            Ihre Rückmeldung wird zusammen mit einem führenden Feedback bearbeitet. Ihr
            eigener Status bleibt unabhängig davon erhalten.
          </p>
        </div>
      )}
    </div>
  )
}

export function FeedbackDuplicates({ entry }: { entry: FeedbackEntry }) {
  const [page, setPage] = useState(1)
  const children = useFeedbackDuplicates(entry.id, page)
  const [editing, setEditing] = useState<{
    kind: 'link' | 'progress'
    entry: FeedbackEntry
  } | null>(null)
  return (
    <section
      className="space-y-3 rounded-lg border p-3"
      aria-label="Duplikate und Fortschritt"
    >
      <h3 className="text-sm font-semibold">Duplikate und Fortschritt</h3>
      {entry.duplicateOfId && (
        <p className="text-sm">
          Duplikat von{' '}
          <Link
            className="text-primary underline"
            to={`/feedback/${entry.duplicateOfId}`}
          >
            {entry.duplicateOfReference}
          </Link>
        </p>
      )}
      <FeedbackProgressSummary entry={entry} />
      <div className="flex flex-wrap gap-2">
        <Button
          variant="outline"
          size="sm"
          disabled={!entry.duplicateVersion || (children.data?.total ?? 0) > 0}
          onClick={() => setEditing({ kind: 'link', entry })}
        >
          {entry.duplicateOfId ? 'Verknüpfung bearbeiten' : 'Als Duplikat verknüpfen'}
        </Button>
        <Button
          variant="outline"
          size="sm"
          disabled={!entry.progressVersion}
          onClick={() => setEditing({ kind: 'progress', entry })}
        >
          Fortschrittshinweis bearbeiten
        </Button>
      </div>
      {children.isPending && (
        <p role="status" className="text-sm text-muted-foreground">
          Verknüpfungen werden geladen …
        </p>
      )}
      {children.isError && (
        <div role="alert" className="text-sm text-destructive">
          Verknüpfungen konnten nicht geladen werden.
          <Button variant="link" onClick={() => void children.refetch()}>
            Erneut versuchen
          </Button>
        </div>
      )}
      {children.data && children.data.total > 0 && (
        <div className="space-y-2">
          <p className="text-sm font-medium">
            Zugehörige Duplikate ({children.data.total})
          </p>
          <p className="text-xs text-muted-foreground">
            Dieser führende Eintrag kann erst nach dem Lösen aller Verknüpfungen selbst
            zum Duplikat werden oder gelöscht werden.
          </p>
          <ul className="space-y-2 text-sm">
            {children.data.items.map((child) => (
              <li key={child.id}>
                <Link className="text-primary underline" to={`/feedback/${child.id}`}>
                  {child.reference} · {child.text}
                </Link>
              </li>
            ))}
          </ul>
          <PageButtons
            page={page}
            total={children.data.total}
            busy={children.isFetching}
            onChange={setPage}
          />
        </div>
      )}
      <Dialog
        open={editing !== null}
        onOpenChange={(open) => {
          if (!open) setEditing(null)
        }}
      >
        {editing?.kind === 'link' && (
          <DuplicateEditor entry={editing.entry} onClose={() => setEditing(null)} />
        )}
        {editing?.kind === 'progress' && (
          <ProgressEditor entry={editing.entry} onClose={() => setEditing(null)} />
        )}
      </Dialog>
    </section>
  )
}

function PageButtons({
  page,
  total,
  busy,
  onChange,
}: {
  page: number
  total: number
  busy: boolean
  onChange: (page: number) => void
}) {
  if (total <= 20 && page === 1) return null
  return (
    <nav aria-label="Verknüpfungsseiten" className="flex items-center gap-2">
      <Button
        type="button"
        variant="ghost"
        size="sm"
        disabled={page <= 1 || busy}
        onClick={() => onChange(page - 1)}
      >
        Vorherige Treffer
      </Button>
      <span className="text-xs text-muted-foreground">Seite {page}</span>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        disabled={page * 20 >= total || busy}
        onClick={() => onChange(page + 1)}
      >
        Weitere Treffer
      </Button>
    </nav>
  )
}

function DuplicateEditor({
  entry,
  onClose,
}: {
  entry: FeedbackEntry
  onClose: () => void
}) {
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [selected, setSelected] = useState<Pick<
    FeedbackDuplicateItem,
    'id' | 'reference'
  > | null>(
    entry.duplicateOfId
      ? { id: entry.duplicateOfId, reference: entry.duplicateOfReference ?? '' }
      : null,
  )
  const [version, setVersion] = useState(entry.duplicateVersion!)
  const [reloadError, setReloadError] = useState(false)
  const targets = useFeedbackDuplicateTargets(entry.id, useDeferredValue(search), page)
  const mutation = useFeedbackDuplicateChange(entry.id)
  const detail = useFeedback(entry.id)
  const conflict = mutation.error instanceof ApiError && mutation.error.status === 409
  async function reload() {
    setReloadError(false)
    const fresh = await detail.refetch()
    if (fresh.isError || !fresh.data?.duplicateVersion) {
      setReloadError(true)
      return
    }
    setVersion(fresh.data.duplicateVersion)
    setSelected(
      fresh.data.duplicateOfId
        ? {
            id: fresh.data.duplicateOfId,
            reference: fresh.data.duplicateOfReference ?? '',
          }
        : null,
    )
    mutation.reset()
    void targets.refetch()
  }
  return (
    <DialogContent className="max-h-[90svh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle>Duplikatverknüpfung</DialogTitle>
        <DialogDescription>
          Wählen Sie das führende Feedback. Eigener Status, Planung und Gespräch bleiben
          erhalten. Die Verknüpfung ist jederzeit lösbar.
        </DialogDescription>
      </DialogHeader>
      <form
        className="space-y-4"
        onSubmit={(event) => {
          event.preventDefault()
          mutation.mutate(
            { duplicateOfId: selected?.id ?? null, duplicateVersion: version },
            {
              onSuccess: () => {
                toast.success('Verknüpfung gespeichert')
                onClose()
              },
            },
          )
        }}
      >
        <fieldset disabled={mutation.isPending || conflict} className="space-y-3">
          <Label htmlFor="duplicate-search">Führendes Feedback suchen</Label>
          <Input
            id="duplicate-search"
            placeholder="Kennung oder Feedbacktext"
            maxLength={200}
            value={search}
            onChange={(event) => {
              setSearch(event.target.value)
              setPage(1)
            }}
          />
          <p role="status" className="text-sm">
            Ausgewählt: {selected?.reference ?? 'Keine Verknüpfung'}
          </p>
          {selected && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setSelected(null)}
            >
              Verknüpfung lösen
            </Button>
          )}
          {targets.isPending && (
            <p role="status" className="text-sm">
              Treffer werden geladen …
            </p>
          )}
          {targets.isError && (
            <div role="alert" className="text-sm text-destructive">
              Die Suche ist fehlgeschlagen.{' '}
              <Button type="button" variant="link" onClick={() => void targets.refetch()}>
                Erneut versuchen
              </Button>
            </div>
          )}
          {targets.isSuccess && targets.data.items.length === 0 && (
            <p className="text-sm text-muted-foreground">
              Kein geeignetes führendes Feedback gefunden.
            </p>
          )}
          <ul className="space-y-2">
            {targets.data?.items.map((target) => (
              <li key={target.id}>
                <Button
                  type="button"
                  variant={selected?.id === target.id ? 'secondary' : 'outline'}
                  className="h-auto w-full justify-start whitespace-normal text-left"
                  aria-pressed={selected?.id === target.id}
                  onClick={() => setSelected(target)}
                >
                  {target.reference} · {target.text}
                </Button>
              </li>
            ))}
          </ul>
          <PageButtons
            page={page}
            total={targets.data?.total ?? 0}
            busy={targets.isFetching}
            onChange={setPage}
          />
        </fieldset>
        <SaveError
          error={mutation.error}
          conflict={conflict}
          reload={reload}
          reloadError={reloadError}
          busy={detail.isFetching}
        />
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            disabled={mutation.isPending}
            onClick={onClose}
          >
            Abbrechen
          </Button>
          <Button type="submit" disabled={mutation.isPending || conflict}>
            {mutation.isPending ? 'Speichert …' : 'Verknüpfung speichern'}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  )
}

function ProgressEditor({
  entry,
  onClose,
}: {
  entry: FeedbackEntry
  onClose: () => void
}) {
  const [note, setNote] = useState(entry.publicProgressNote ?? '')
  const [version, setVersion] = useState(entry.progressVersion!)
  const [reloadError, setReloadError] = useState(false)
  const mutation = useFeedbackPublishProgress(entry.id)
  const detail = useFeedback(entry.id)
  const conflict = mutation.error instanceof ApiError && mutation.error.status === 409
  async function reload() {
    setReloadError(false)
    const fresh = await detail.refetch()
    if (fresh.isError || !fresh.data?.progressVersion) {
      setReloadError(true)
      return
    }
    setNote(fresh.data.publicProgressNote ?? '')
    setVersion(fresh.data.progressVersion)
    mutation.reset()
  }
  return (
    <DialogContent>
      <DialogHeader>
        <DialogTitle>Fortschrittshinweis freigeben</DialogTitle>
        <DialogDescription>
          Dieser Text wird sofort für den Einreicher und für Einreicher aller zugehörigen
          Duplikate sichtbar. Verwenden Sie nur Informationen, die Sie für diese Personen
          freigeben möchten. Ein leeres Feld entfernt den aktuellen Hinweis.
        </DialogDescription>
      </DialogHeader>
      <form
        className="space-y-4"
        onSubmit={(event) => {
          event.preventDefault()
          mutation.mutate(
            { note: note.trim() || null, progressVersion: version },
            {
              onSuccess: () => {
                toast.success('Fortschrittshinweis gespeichert')
                onClose()
              },
            },
          )
        }}
      >
        <Label htmlFor="public-progress-note">Freigegebener Fortschrittshinweis</Label>
        <Textarea
          id="public-progress-note"
          value={note}
          maxLength={2000}
          rows={5}
          disabled={mutation.isPending || conflict}
          onChange={(event) => setNote(event.target.value)}
        />
        <SaveError
          error={mutation.error}
          conflict={conflict}
          reload={reload}
          reloadError={reloadError}
          busy={detail.isFetching}
        />
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            disabled={mutation.isPending}
            onClick={onClose}
          >
            Abbrechen
          </Button>
          <Button type="submit" disabled={mutation.isPending || conflict}>
            {mutation.isPending
              ? 'Speichert …'
              : note.trim()
                ? 'Hinweis freigeben'
                : 'Hinweis entfernen'}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  )
}

function SaveError({
  error,
  conflict,
  reload,
  reloadError,
  busy,
}: {
  error: Error | null
  conflict: boolean
  reload: () => Promise<void>
  reloadError: boolean
  busy: boolean
}) {
  return (
    <>
      {error && (
        <div role="alert" className="space-y-2 text-sm text-destructive">
          <p>
            {error instanceof ApiError
              ? error.userMessage()
              : 'Die Änderung konnte nicht gespeichert werden.'}
          </p>
          {conflict && (
            <>
              <p>
                Laden Sie den aktuellen Stand. Ihre Eingaben im Dialog werden dabei
                ersetzt.
              </p>
              <Button
                type="button"
                variant="outline"
                disabled={busy}
                onClick={() => void reload()}
              >
                Aktuellen Stand laden
              </Button>
            </>
          )}
        </div>
      )}
      {reloadError && (
        <p role="alert" className="text-sm text-destructive">
          Der aktuelle Stand konnte nicht geladen werden. Bitte versuchen Sie es erneut.
        </p>
      )}
    </>
  )
}
